import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout, { PageHeader } from "@/components/Layout";
import { DiscBadge, PriorityBadge, StatusChip } from "@/components/badges";
import HazardBanner, { ReliabilityChip } from "@/components/HazardBanner";
import { toast } from "sonner";
import { Search, History, MapPin, Plus, ShieldAlert, KeyRound, ClipboardCheck, Sparkles, ListChecks, X, TrendingUp } from "lucide-react";
import { FrequentLocations } from "@/components/FrequentLocations";

const WRITE_ROLES = ["call_taker", "police_dispatcher", "fire_dispatcher", "ems_dispatcher", "supervisor", "watch_commander", "records_supervisor", "admin"];
const HAZARD_TYPES = ["WEAPONS", "VIOLENT PERSON", "THREAT TO RESPONDERS", "BARRICADE", "HAZMAT", "DANGEROUS ANIMAL", "STRUCTURAL", "CONTAGIOUS", "OXYGEN IN USE"];
const RELIABILITY = ["REPORTED", "CORROBORATED", "CONFIRMED", "OUTDATED", "RESOLVED"];

export default function Premise() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [sp] = useSearchParams();
  const [addr, setAddr] = useState(sp.get("address") || "412 Harbor Street");
  const [disc, setDisc] = useState("police");
  const [match, setMatch] = useState(null);
  const [premises, setPremises] = useState([]);
  const [showAddr, setShowAddr] = useState(false);
  const [newAddr, setNewAddr] = useState({ address: "", apt: "", building_wide: true, responsible_party: "", contacts: "", access_notes: "", fire_ems_staging: "" });
  const [hz, setHz] = useState({ premiseId: "", type: "WEAPONS", detail: "", weapon_type: "", reliability: "REPORTED", source: "", reported_date: "", review_date: "", handling_instructions: "", escalate: false });
  const [catalog, setCatalog] = useState([]);
  const [legend, setLegend] = useState([]);
  const [showInd, setShowInd] = useState(false);
  const [indSel, setIndSel] = useState([]);
  const [savingInd, setSavingInd] = useState(false);

  const runMatch = useCallback(async (a, d) => {
    const { data } = await client.get("/premise-match", { params: { address: a, discipline: d } });
    setMatch(data);
  }, []);
  const loadPremises = async () => { const { data } = await client.get("/premises"); setPremises(data); };
  useEffect(() => { if (addr) runMatch(addr, disc); loadPremises(); client.get("/premise-indicators").then((r) => { setCatalog(r.data.catalog); setLegend(r.data.legend); }).catch(() => {}); }, []); // eslint-disable-line

  const createAddr = async () => {
    if (!newAddr.address.trim()) { toast.error("Address required"); return; }
    await client.post("/premises", newAddr);
    toast.success("Known address added"); setShowAddr(false);
    setNewAddr({ address: "", apt: "", building_wide: true, responsible_party: "", contacts: "", access_notes: "", fire_ems_staging: "" });
    loadPremises();
  };
  const addHazard = async () => {
    if (!hz.premiseId) { toast.error("Select a premise"); return; }
    await client.post(`/premises/${hz.premiseId}/hazards`, hz);
    toast.success("Hazard added" + (hz.escalate ? " — supervisor escalated" : ""));
    setHz({ ...hz, detail: "", weapon_type: "", source: "", handling_instructions: "", escalate: false });
    runMatch(addr, disc); loadPremises();
  };
  const openIndEditor = () => {
    if (!p) return;
    setIndSel((p.indicators || []).map((i) => i.code));
    setShowInd(true);
  };
  const toggleInd = (code) => setIndSel((s) => s.includes(code) ? s.filter((c) => c !== code) : [...s, code]);
  const saveIndicators = async () => {
    if (!p) return;
    setSavingInd(true);
    try { await client.patch(`/premises/${p.id}/indicators`, { indicators: indSel }); toast.success("Premise indicators updated"); setShowInd(false); runMatch(addr, disc); loadPremises(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save indicators"); }
    finally { setSavingInd(false); }
  };

  const p = match?.premise;

  return (
    <Layout>
      <PageHeader title="Premise Intelligence" sub="Known addresses · hazard alerts · last 10 CFS · verified, time-stamped, role-based"
        right={
          <div className="flex gap-1">
            {["police", "fire", "ems"].map((d) => (
              <button key={d} data-testid={`premise-disc-${d}`} onClick={() => { setDisc(d); runMatch(addr, d); }}
                className={`px-3 py-1.5 text-xs font-head border transition-colors ${disc === d ? "border-transparent text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}
                style={disc === d ? { background: d === "police" ? "#3B82F6" : d === "fire" ? "#EF4444" : "#10B981" } : {}}>
                {d.toUpperCase()}
              </button>
            ))}
          </div>
        } />

      <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[320px_1fr]">
        {/* left: known-address management */}
        <div className="border-r border-[#2C2D35] bg-[#0d0e11] overflow-y-auto">
          <div className="px-3 py-2 border-b border-[#2C2D35] flex items-center justify-between">
            <span className="font-head text-xs font-semibold flex items-center gap-1.5"><MapPin size={13} /> KNOWN ADDRESSES ({premises.length})</span>
            {canWrite && <button data-testid="add-address-toggle" onClick={() => setShowAddr(!showAddr)} className="text-[#3B82F6] hover:text-white"><Plus size={15} /></button>}
          </div>
          {showAddr && (
            <div className="p-3 border-b border-[#2C2D35] space-y-1.5 bg-[#141518]">
              {[["address", "Address *"], ["apt", "Apt/Unit (blank = whole bldg)"], ["responsible_party", "Responsible party"], ["contacts", "Contact #"], ["access_notes", "Access notes"], ["fire_ems_staging", "Fire/EMS staging"]].map(([k, ph]) => (
                <input key={k} data-testid={`new-addr-${k}`} value={newAddr[k]} onChange={(e) => setNewAddr({ ...newAddr, [k]: e.target.value })}
                  placeholder={ph} className="w-full bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none focus:border-[#3B82F6] font-mono" />
              ))}
              <label className="flex items-center gap-2 text-[11px] font-mono text-[#9CA3AF]">
                <input type="checkbox" data-testid="new-addr-buildingwide" checked={newAddr.building_wide} onChange={(e) => setNewAddr({ ...newAddr, building_wide: e.target.checked })} /> Building-wide alert
              </label>
              <button data-testid="save-address-btn" onClick={createAddr} className="w-full py-1.5 text-xs font-head bg-[#3B82F6] text-white">Save Address</button>
            </div>
          )}
          {premises.map((pr) => (
            <button key={pr.id} data-testid={`premise-item-${pr.id}`} onClick={() => { setAddr(pr.address); runMatch(pr.address, disc); }}
              className="w-full text-left px-3 py-2 border-b border-[#2C2D35] hover:bg-[#141518] transition-colors">
              <div className="text-sm font-head">{pr.address}{pr.apt ? ` #${pr.apt}` : ""}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {(pr.hazards || []).slice(0, 3).map((h, i) => <span key={i} className="font-mono text-[9px] text-[#EF4444] border border-[#DC2626] px-1">{h.type}</span>)}
                {(pr.hazards || []).length === 0 && <span className="font-mono text-[9px] text-[#9CA3AF]">no hazards</span>}
              </div>
            </button>
          ))}

          {canWrite && (
            <div className="p-3 border-t border-[#2C2D35] space-y-1.5">
              <div className="font-head text-[11px] font-semibold flex items-center gap-1.5 text-[#F59E0B]"><ShieldAlert size={12} /> ADD HAZARD / CAUTION</div>
              <select data-testid="hz-premise" value={hz.premiseId} onChange={(e) => setHz({ ...hz, premiseId: e.target.value })} className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono outline-none">
                <option value="">Select premise…</option>
                {premises.map((pr) => <option key={pr.id} value={pr.id}>{pr.address}{pr.apt ? ` #${pr.apt}` : ""}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-1.5">
                <select data-testid="hz-type" value={hz.type} onChange={(e) => setHz({ ...hz, type: e.target.value })} className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono outline-none">{HAZARD_TYPES.map((x) => <option key={x}>{x}</option>)}</select>
                <select data-testid="hz-reliability" value={hz.reliability} onChange={(e) => setHz({ ...hz, reliability: e.target.value })} className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs font-mono outline-none">{RELIABILITY.map((x) => <option key={x}>{x}</option>)}</select>
              </div>
              <input data-testid="hz-detail" value={hz.detail} onChange={(e) => setHz({ ...hz, detail: e.target.value })} placeholder="Detail" className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none font-mono" />
              <div className="grid grid-cols-2 gap-1.5">
                <input data-testid="hz-weapon" value={hz.weapon_type} onChange={(e) => setHz({ ...hz, weapon_type: e.target.value })} placeholder="Weapon type" className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none font-mono" />
                <input data-testid="hz-source" value={hz.source} onChange={(e) => setHz({ ...hz, source: e.target.value })} placeholder="Source" className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <input data-testid="hz-reported" type="date" value={hz.reported_date} onChange={(e) => setHz({ ...hz, reported_date: e.target.value })} className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none font-mono text-[#9CA3AF]" />
                <input data-testid="hz-review" type="date" value={hz.review_date} onChange={(e) => setHz({ ...hz, review_date: e.target.value })} className="bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none font-mono text-[#9CA3AF]" />
              </div>
              <input data-testid="hz-handling" value={hz.handling_instructions} onChange={(e) => setHz({ ...hz, handling_instructions: e.target.value })} placeholder="Special handling instructions" className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none font-mono" />
              <label className="flex items-center gap-2 text-[11px] font-mono text-[#F59E0B]"><input type="checkbox" data-testid="hz-escalate" checked={hz.escalate} onChange={(e) => setHz({ ...hz, escalate: e.target.checked })} /> Escalate to supervisor</label>
              <button data-testid="add-hazard-btn" onClick={addHazard} className="w-full py-1.5 text-xs font-head bg-[#F59E0B] text-black font-semibold">Add Hazard</button>
            </div>
          )}
        </div>

        {/* right: intelligence card */}
        <div className="overflow-y-auto p-4">
          <div className="mb-4 max-w-4xl">
            <FrequentLocations />
          </div>
          <div className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-3 max-w-2xl mb-4">
            <Search size={15} className="text-[#9CA3AF]" />
            <input data-testid="premise-search" value={addr} onChange={(e) => setAddr(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runMatch(addr, disc)}
              placeholder="Search any address…" className="flex-1 bg-transparent py-2 text-sm outline-none font-mono" />
            <button data-testid="premise-search-btn" onClick={() => runMatch(addr, disc)} className="px-3 py-1 text-xs font-head bg-[#3B82F6] text-white">Search</button>
          </div>

          {match && !match.matched && (
            <div className="text-xs font-mono text-[#9CA3AF] border border-[#2C2D35] p-3 max-w-2xl">No premise intelligence card on file for this address. {match.last_calls.length} prior call(s) below.</div>
          )}

          {match && match.matched && (
            <>
              <HazardBanner key={p.id + disc} summary={match.summary} hazards={match.hazards}
                fireEmsStaging={p.fire_ems_staging} announce={true} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div className="border border-[#2C2D35]">
                  <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center justify-between">
                    <span className="flex items-center gap-2"><ListChecks size={13} /> PREMISE INDICATORS ({(match.indicators || []).length})</span>
                    {canWrite && <button data-testid="edit-indicators-btn" onClick={openIndEditor} className="text-[#8B5CF6] hover:text-white font-mono text-[10px] flex items-center gap-1"><Sparkles size={11} /> Edit</button>}
                  </div>
                  <div className="p-3 flex flex-wrap gap-1.5" data-testid="premise-indicator-chips">
                    {(match.indicators || []).map((ind, i) => (
                      <span key={i} className="font-mono text-[10px] px-2 py-1 rounded" style={{ background: ind.hex + "22", color: ind.hex, border: `1px solid ${ind.hex}` }}>{ind.label}</span>
                    ))}
                    {(match.indicators || []).length === 0 && <span className="text-[11px] font-mono text-[#9CA3AF]">No indicators set for this premise.</span>}
                  </div>
                </div>
                <div className="border border-[#2C2D35]">
                  <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2 text-[#F59E0B]"><ShieldAlert size={13} /> DISPATCH RECOMMENDATIONS</div>
                  <div className="p-3 space-y-1.5" data-testid="premise-recommendations">
                    {(match.recommendations || []).map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs font-mono text-[#F59E0B]"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> {r}</div>
                    ))}
                    {(match.recommendations || []).length === 0 && <span className="text-[11px] font-mono text-[#9CA3AF]">No automated recommendations. Dispatcher discretion applies.</span>}
                  </div>
                </div>
              </div>

              {match.call_history && (
                <div className="border border-[#2C2D35] mb-4" data-testid="premise-call-history">
                  <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2"><TrendingUp size={13} /> CALL HISTORY INTELLIGENCE</div>
                  <div className="p-3">
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                      {[["24 HRS", match.call_history.last_24h], ["7 DAYS", match.call_history.last_7d], ["30 DAYS", match.call_history.last_30d], ["1 YEAR", match.call_history.last_year], ["TOTAL", match.call_history.total], ["WARRANTS", match.call_history.prior_warrants]].map(([l, v]) => (
                        <div key={l} className="text-center border border-[#2C2D35] py-2 bg-[#0d0e11]">
                          <div className="font-head font-bold text-lg text-[#3B82F6]">{v}</div>
                          <div className="font-mono text-[9px] text-[#9CA3AF]">{l}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {match.call_history.repeat_address && <span className="font-mono text-[10px] px-2 py-0.5 bg-[#DC2626]/20 text-[#F87171] border border-[#DC2626]">REPEAT ADDRESS</span>}
                      <span className="font-mono text-[10px] text-[#9CA3AF]">Top call types:</span>
                      {(match.call_history.top_call_types || []).map((t, i) => (
                        <span key={i} className="font-mono text-[10px] px-2 py-0.5 bg-[#141518] border border-[#2C2D35] text-[#D1D5DB]">{t.type} ({t.count})</span>
                      ))}
                      {(match.call_history.top_call_types || []).length === 0 && <span className="font-mono text-[10px] text-[#9CA3AF]">none</span>}
                    </div>
                  </div>
                </div>
              )}


              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-[#2C2D35]">
                  <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2"><KeyRound size={13} /> ACCESS & CONTACTS</div>
                  <div className="p-3 text-xs font-mono space-y-1">
                    <div>Responsible party: {p.responsible_party || "—"}</div>
                    <div>Contact: {p.contacts || "—"}</div>
                    <div className="text-[#9CA3AF]">Gate/access codes: {p.gate_codes ? "ON FILE (restricted)" : "None"}</div>
                    <div>Access: {p.access_notes || "—"}</div>
                    <div className="text-[#F59E0B]">Fire/EMS staging: {p.fire_ems_staging || "—"}</div>
                    <div className="text-[#9CA3AF]">{p.building_wide ? "Building-wide alert" : `Unit-specific: ${p.apt || "—"}`}</div>
                  </div>
                  <div className="px-3 py-2 border-t border-[#2C2D35] font-head text-[11px] font-semibold flex items-center gap-1.5"><ClipboardCheck size={12} /> ACKNOWLEDGMENTS ({(p.acknowledgments || []).length})</div>
                  <div className="p-2 max-h-28 overflow-y-auto">
                    {(p.acknowledgments || []).length === 0 && <div className="text-[11px] font-mono text-[#9CA3AF] px-1">None yet.</div>}
                    {(p.acknowledgments || []).map((a, i) => (
                      <div key={i} className="font-mono text-[10px] text-[#9CA3AF]">{new Date(a.ts).toLocaleString("en-GB", { hour12: false })} · {a.user}{a.unit ? ` (${a.unit})` : ""} · {a.device}{a.incident_cc ? ` · ${a.incident_cc}` : ""}</div>
                    ))}
                  </div>
                </div>

                <div className="border border-[#2C2D35]">
                  <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2"><History size={13} /> LAST 10 CALLS FOR SERVICE</div>
                  <div className="divide-y divide-[#2C2D35]">
                    {match.last_calls.length === 0 && <div className="p-3 text-xs font-mono text-[#9CA3AF]">No prior calls.</div>}
                    {match.last_calls.map((c, i) => (
                      <div key={i} data-testid={`last-call-${i}`} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <PriorityBadge priority={c.priority} /><DiscBadge discipline={c.discipline} /><StatusChip status={c.status} />
                          <span className="font-mono text-[10px] text-[#9CA3AF] ml-auto">{c.cc_number}</span>
                        </div>
                        <div className="text-sm mt-0.5">{c.call_type_label}</div>
                        <div className="font-mono text-[10px] text-[#9CA3AF]">{new Date(c.created_at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border border-[#2C2D35] mt-4 max-w-4xl">
                <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">FULL HAZARD RECORD & RELIABILITY</div>
                <div className="p-3 space-y-2">
                  {(p.hazards || []).map((h, i) => (
                    <div key={i} className="border-l-2 border-[#2C2D35] pl-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-head font-bold text-sm">{h.type}</span><ReliabilityChip value={h.reliability} />
                        {h.weapon_type && <span className="font-mono text-[10px] text-[#EF4444]">{h.weapon_type}</span>}
                      </div>
                      <div className="text-xs">{h.detail}</div>
                      <div className="font-mono text-[9px] text-[#9CA3AF]">{h.source ? `Source: ${h.source} · ` : ""}Reported {h.reported_date || "?"} · Review {h.review_date || "?"}{h.related_incident ? ` · ${h.related_incident}` : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showInd && p && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="indicator-editor-modal">
          <div className="w-full max-w-3xl bg-[#141518] border border-[#2C2D35] my-8">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2C2D35]">
              <div className="font-head font-bold text-sm flex items-center gap-2"><ListChecks size={15} /> Premise Indicators — {p.address}</div>
              <button data-testid="indicator-modal-close" onClick={() => setShowInd(false)} className="text-[#9CA3AF] hover:text-white"><X size={18} /></button>
            </div>
            <div className="px-4 py-2 border-b border-[#2C2D35] flex flex-wrap gap-2">
              {legend.map((l) => (
                <span key={l.color} className="font-mono text-[9px] px-1.5 py-0.5 rounded" style={{ background: l.hex + "22", color: l.hex, border: `1px solid ${l.hex}` }}>{l.meaning}</span>
              ))}
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-4">
              {catalog.map((cat) => (
                <div key={cat.category}>
                  <div className="font-head text-[11px] font-bold uppercase mb-1.5 flex items-center gap-1.5" style={{ color: cat.hex }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: cat.hex }} /> {cat.category_label}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {cat.indicators.map((ind) => {
                      const on = indSel.includes(ind.code);
                      return (
                        <div key={ind.code} data-testid={`ind-opt-${ind.code}`} onClick={() => toggleInd(ind.code)} role="button"
                          className={`flex items-center gap-2 px-2 py-1 text-[11px] font-mono cursor-pointer border transition-colors ${on ? "text-white" : "text-[#9CA3AF] border-[#2C2D35] hover:border-[#4B5563]"}`}
                          style={on ? { background: cat.hex + "22", borderColor: cat.hex } : {}}>
                          <input type="checkbox" readOnly checked={on} className="pointer-events-none" /> {ind.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#2C2D35]">
              <span className="font-mono text-[11px] text-[#9CA3AF]">{indSel.length} indicator(s) selected</span>
              <div className="flex gap-2">
                <button onClick={() => setShowInd(false)} className="px-3 py-2 text-sm font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white">Cancel</button>
                <button data-testid="save-indicators-btn" onClick={saveIndicators} disabled={savingInd} className="px-4 py-2 text-sm font-head bg-[#8B5CF6] text-white font-semibold disabled:opacity-50">Save Indicators</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
